import { BillingRepository } from '@/core/repositories/billing.repository'
import {
    BillingCalculateCostParams,
    BillingCalculateCostResponseT,
    BillingChangeAccountantPayload,
    BillingDiscountPayloadT,
    BillingGetAvrForPrintResponseT,
    BillingGetCalculationResponseT,
    BillingGetCalculationsParamsT,
    BillingGetCalculationsResponseT,
    BillingGetDiscountsResponseT,
    BillingGetIndividualCompaniesParamsT,
    BillingGetIndividualCompaniesResponseT,
    BillingGetInvoiceCalculationResponseT,
    BillingGetTariffByIdResponseT,
    BillingGetTariffResponseT,
    BillingGetTariffSecondByIdResponseT,
    BillingGetTariffThirdByIdResponseT,
    BillingGetTariffsResponseT,
    BillingGetTranscriptForPrintResponseT,
    BillingGetTranscriptResponseT,
    BillingGetTranscriptsParams,
    BillingGetTranscriptsResponseT,
    BillingGetTtnForPrintResponseT,
    BillingPreviewTranscriptParamsT,
    BillingPreviewTranscriptResponseT,
    CalculationPayloadT,
    CreateTariffPayloadT,
    IndividualTariffAdditionalServicesPayloadT,
    IndividualTariffParamsPayloadT,
    IndividualTariffPayloadT,
    TariffPayloadT,
    UpdateCalculationPayload,
    UpdateTranscriptPayload,
    ManualGenerateTranscriptPayload,
    BillingGetAdditionalServicesPricesParams,
    BillingGetAdditionalServicesPricesResponse,
    BillingUpdateCalculationPricePayload,
    BillingGetCompanyResponseT,
    GetAdditionalServicesTariffsParams,
    InternationalCalculationPayloadT,
    SetCostResponse,
    BillingSetVatPayload,
    GetFtlCalculatorsPayload,
    GetFtlCalculatorsResponse,
    GetFtlTariffsParams,
    GetFtlTariffsResponse,
    GetShipmentCostsFtlParams,
    GetShipmentCostsFtlResponse,
    BillingCalculateFtlCostParams,
    BillingCalculateCostFtlResponseT,
    FtlTariffPayload,
    BillingGetFtlTariffByIdResponse,
    GetStandard2025TariffsResponse,
    GetMinWeightResponse,
} from '../types/billing.types'
import { HistoryInvoiceBillingT } from '@/core/types/invoices.types'

const billingRepository = new BillingRepository()

export class BillingService {
    async getCalculations(
        params: BillingGetCalculationsParamsT
    ): Promise<BillingGetCalculationsResponseT> {
        const response = await billingRepository.getCalculations(params)
        return response.data
    }

    async getMinWeight(id: number): Promise<GetMinWeightResponse> {
        const response = await billingRepository.getMinWeight(id)
        return response.data
    }

    async previewTranscript(
        params: BillingPreviewTranscriptParamsT
    ): Promise<BillingPreviewTranscriptResponseT> {
        const response = await billingRepository.previewTranscript(params)
        return response.data
    }

    async getCalculation(calculation_id: number): Promise<BillingGetCalculationResponseT> {
        const response = await billingRepository.getCalculation(calculation_id)
        return response.data
    }

    async getTariffs(page: number): Promise<BillingGetTariffsResponseT> {
        const response = await billingRepository.getTariffs(page)
        return response.data
    }

    async getTariffsStandard(): Promise<BillingGetTariffsResponseT> {
        const response = await billingRepository.getTariffsStandard()
        return response.data
    }

    async getCompaniesIndividual(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<BillingGetIndividualCompaniesResponseT> {
        const response = await billingRepository.getCompaniesIndividual(params)
        return response.data
    }

    async getCompaniesFtl(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<BillingGetIndividualCompaniesResponseT> {
        const response = await billingRepository.getCompaniesFtl(params)
        return response.data
    }

    async getCompaniesUnique(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<BillingGetIndividualCompaniesResponseT> {
        const response = await billingRepository.getCompaniesUnique(params)
        return response.data
    }

    async getInvoiceCalculation(
        invoice_id: number
    ): Promise<BillingGetInvoiceCalculationResponseT> {
        const response = await billingRepository.getInvoiceCalculation(invoice_id)
        return response.data
    }

    async getTranscriptForPrint(
        invoice_number: string
    ): Promise<BillingGetTranscriptForPrintResponseT> {
        const response = await billingRepository.getTranscriptForPrint(invoice_number)
        return response.data
    }

    async getAvrForPrint(invoice_number: string): Promise<BillingGetAvrForPrintResponseT> {
        const response = await billingRepository.getAvrForPrint(invoice_number)
        return response.data
    }

    async getTariff(tariff_id: number): Promise<BillingGetTariffResponseT> {
        const response = await billingRepository.getTariff(tariff_id)
        return response.data
    }

    async getTariffByCompanyId(companyId: number): Promise<BillingGetTariffByIdResponseT> {
        const response = await billingRepository.getTariffByCompanyId(companyId)
        return response.data
    }

    async getFtlTariffsByCompanyId(companyId: number): Promise<BillingGetFtlTariffByIdResponse> {
        const response = await billingRepository.getFtlTariffsByCompanyId(companyId)
        return response.data
    }

    async getTariffSecondByCompanyId(
        companyId: number
    ): Promise<BillingGetTariffSecondByIdResponseT> {
        const response = await billingRepository.getTariffSecondByCompanyId(companyId)
        return response.data
    }

    async getTariffThirdByCompanyId(
        params: GetAdditionalServicesTariffsParams
    ): Promise<BillingGetTariffThirdByIdResponseT> {
        const response = await billingRepository.getTariffThirdByCompanyId(params)
        return response.data
    }

    async createCalculation(payload: CalculationPayloadT): Promise<void> {
        const response = await billingRepository.createCalculation(payload)
        return response.data
    }

    async setCost(id: number): Promise<SetCostResponse> {
        const response = await billingRepository.setCost(id)
        return response.data
    }

    async createInternationalCalculation(payload: InternationalCalculationPayloadT): Promise<void> {
        const response = await billingRepository.createInternationalCalculation(payload)
        return response.data
    }

    async addIndividualTariff(payload: IndividualTariffPayloadT): Promise<void> {
        const response = await billingRepository.addIndividualTariff(payload)
        return response.data
    }

    async addFtlTariff(payload: FtlTariffPayload): Promise<void> {
        const response = await billingRepository.addFtlTariff(payload)
        return response.data
    }

    async editIndividualTariff(payload: IndividualTariffPayloadT): Promise<void> {
        const response = await billingRepository.editIndividualTariff(payload)
        return response.data
    }

    async addIndividualTariffParams(payload: IndividualTariffParamsPayloadT): Promise<void> {
        const response = await billingRepository.addIndividualTariffParams(payload)
        return response.data
    }

    async editIndividualTariffParams(payload: IndividualTariffParamsPayloadT): Promise<void> {
        const response = await billingRepository.editIndividualTariffParams(payload)
        return response.data
    }

    async addAdditionalServices(
        payload: IndividualTariffAdditionalServicesPayloadT
    ): Promise<void> {
        const response = await billingRepository.addAdditionalServices(payload)
        return response.data
    }

    async editAdditionalServices(
        payload: IndividualTariffAdditionalServicesPayloadT
    ): Promise<void> {
        const response = await billingRepository.editAdditionalServices(payload)
        return response.data
    }

    async handleBilling(params: HistoryInvoiceBillingT): Promise<void> {
        const response = await billingRepository.handleBilling(params)
        return response.data
    }

    async getTtnForPrint(invoice_number: string): Promise<BillingGetTtnForPrintResponseT> {
        const response = await billingRepository.getTtnForPrint(invoice_number)
        return response.data
    }

    async updateTariff(tariff_id: number, payload: TariffPayloadT): Promise<void> {
        const response = await billingRepository.updateTariff(tariff_id, payload)
        return response.data
    }

    async updateTranscript(transcript_id: number, payload: UpdateTranscriptPayload): Promise<void> {
        const response = await billingRepository.updateTranscript(transcript_id, payload)
        return response.data
    }

    async updateCalculation(
        calculation_id: number,
        payload: UpdateCalculationPayload
    ): Promise<void> {
        const response = await billingRepository.updateCalculation(calculation_id, payload)
        return response.data
    }

    async getTranscripts(
        params: BillingGetTranscriptsParams
    ): Promise<BillingGetTranscriptsResponseT> {
        const response = await billingRepository.getTranscripts(params)
        return response.data
    }

    async getTranscript(transcript_id: number): Promise<BillingGetTranscriptResponseT> {
        const response = await billingRepository.getTranscript(transcript_id)
        return response.data
    }

    async downloadFile(file_id: number): Promise<any> {
        const response = await billingRepository.downloadFile(file_id)
        return response.data
    }

    async download1CFile(transcript_id: number): Promise<any> {
        const response = await billingRepository.download1CFile(transcript_id)
        return response.data
    }

    async generateCompanyTranscript(company_id: number): Promise<void> {
        const response = await billingRepository.generateCompanyTranscript(company_id)
        return response.data
    }

    async exportTranscripts(start_date: string, end_date: string): Promise<void> {
        const response = await billingRepository.exportTranscripts(start_date, end_date)
        return response.data
    }

    async createTariff(payload: CreateTariffPayloadT): Promise<void> {
        const response = await billingRepository.createTariff(payload)
        return response.data
    }

    async getDiscounts(page: number): Promise<BillingGetDiscountsResponseT> {
        const response = await billingRepository.getDiscounts(page)
        return response.data
    }

    async createDiscount(payload: BillingDiscountPayloadT): Promise<void> {
        const response = await billingRepository.createDiscount(payload)
        return response.data
    }

    async printWaybill(invoice_id: number): Promise<any> {
        const response = await billingRepository.printWaybill(invoice_id)
        return response.data
    }

    async getCompany(company_id: number): Promise<BillingGetCompanyResponseT> {
        const response = await billingRepository.getCompany(company_id)
        return response.data
    }

    async applyAccountantToCompany(company_id: number, accountant_id: number): Promise<void> {
        const response = await billingRepository.applyAccountantToCompany(company_id, accountant_id)
        return response.data
    }

    async changeAccountant(payload: BillingChangeAccountantPayload): Promise<void> {
        const response = await billingRepository.changeAccountant(payload)
        return response.data
    }

    async calculateCost(
        params: BillingCalculateCostParams
    ): Promise<BillingCalculateCostResponseT> {
        const response = await billingRepository.calculateCost(params)
        return response.data
    }

    async calculateCostV3(
        params: BillingCalculateCostParams
    ): Promise<BillingCalculateCostResponseT> {
        const response = await billingRepository.calculateCostV3(params)
        return response.data
    }

    async calculateCostFtl(
        params: BillingCalculateFtlCostParams
    ): Promise<BillingCalculateCostFtlResponseT> {
        const response = await billingRepository.calculateCostFtl(params)
        return response.data
    }

    async deleteTranscript(transcript_id: number): Promise<void> {
        await billingRepository.deleteTranscript(transcript_id)
    }

    async manualGenerateTranscript(payload: ManualGenerateTranscriptPayload): Promise<void> {
        const response = await billingRepository.manualGenerateTranscript(payload)
        return response.data
    }

    async getFtlCalculators(payload: GetFtlCalculatorsPayload): Promise<GetFtlCalculatorsResponse> {
        const response = await billingRepository.getFtlCalculators(payload)
        return response.data
    }

    async getShipmentCostsFtl(
        params: GetShipmentCostsFtlParams
    ): Promise<GetShipmentCostsFtlResponse> {
        const response = await billingRepository.getShipmentCostsFtl(params)
        return response.data
    }

    async getFtlTariffs(params: GetFtlTariffsParams): Promise<GetFtlTariffsResponse> {
        const response = await billingRepository.getFtlTariffs(params)
        return response.data
    }

    async updateFewCalculations(calculationIds: number[]): Promise<void> {
        const response = await billingRepository.updateFewCalculations(calculationIds)
        return response.data
    }

    async getAdditionalServicesPrices(
        params: BillingGetAdditionalServicesPricesParams
    ): Promise<BillingGetAdditionalServicesPricesResponse> {
        const response = await billingRepository.getAdditionalServicesPrices(params)
        return response.data
    }

    async updateCalculationPrice(
        calculation_id: number,
        payload: BillingUpdateCalculationPricePayload
    ): Promise<void> {
        const response = await billingRepository.updateCalculationPrice(calculation_id, payload)
        return response.data
    }

    async setVat(company_id: number, payload: BillingSetVatPayload): Promise<void> {
        const response = await billingRepository.setVat(company_id, payload)
        return response.data
    }

    async importInvoices(form_data: FormData): Promise<void> {
        const response = await billingRepository.importInvoices(form_data)
        return response.data
    }

    async getStandard2025Tariffs(zoneId: number): Promise<GetStandard2025TariffsResponse> {
        const response = await billingRepository.getStandard2025Tariffs(zoneId)
        return response.data
    }

    async createDefaultTariffV3(payload: { weight: number; companyId: number }): Promise<void> {
        const response = await billingRepository.createDefaultTariffV3(payload)
        return response.data
    }
}
